import CreateIcon from "@mui/icons-material/Create";
import { Box, Stack, Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { Button, Create, SimpleForm, useDataProvider } from "react-admin";
import { FieldValues } from "react-hook-form";
import CustomDialog from "shared/components/dialogs/CustomDialog";
import { SiteContryEnum } from "shared/enums/globalEnums";
import { validateCreateForm } from "../validations";
import GroupAssociationDisplay from "./summaries/GroupAssociationDisplay";
import GroupSiteForm from "./GroupSiteForm";
import { useCreateForm } from "./hooks/useCreateForm";
import PeMetricsFormContainer from "./PeMetricsForm";
import { GroupSiteAssociationFormValues } from "../types";

import MetricsSummary from "./summaries/MetricsSummary";
import AssociationOperationsSummary from "./summaries/AssociationOperationsSummary";

const FINLAND_METRICS_FORM_ID = "finland-site-metrics-form";

export const GroupSiteCreate = () => {
  const [country, setCountry] = useState<SiteContryEnum>(SiteContryEnum.France);
  return (
    <Create>
      <Box>
        <Tabs value={country} onChange={(_, newValue: SiteContryEnum) => setCountry(newValue)}>
          <Tab value={SiteContryEnum.France} label="France" />
          <Tab value={SiteContryEnum.Finland} label="Finland" />
        </Tabs>
        <Box>
          {country === SiteContryEnum.France && <GroupSiteCreateByCountry country={SiteContryEnum.France} />}
          {country === SiteContryEnum.Finland && <GroupSiteCreateByCountry country={SiteContryEnum.Finland} />}
        </Box>
      </Box>
    </Create>
  );
};

const GroupSiteCreateByCountry = ({ country }: { country: SiteContryEnum }) => {
  const dataprovider = useDataProvider();
  const {
    handleSavePeMetrics,
    handleClose,
    handleSubmitAssociations,
    handleFinalSubmit,
    siteFinlandServiceInfo,
    open,
    isLoading,
    metricValues,
    associationValues,
    displayAssociationOperationsSummaryOnly,
    displayAssociationAndMetricOperationsSummary,
    displayPeMetricsForm,
  } = useCreateForm({ country });

  return (
    <>
      <SimpleForm
        onSubmit={(values: FieldValues) => handleSubmitAssociations(values as GroupSiteAssociationFormValues)}
        validate={validateCreateForm({ dataprovider, country })}
        sanitizeEmptyValues
      >
        <GroupSiteForm country={country} />
        <GroupAssociationDisplay />
      </SimpleForm>
      <CustomDialog
        open={open}
        onClose={handleClose}
        title="Associations"
        actionComponentContent={
          <Stack direction="row" spacing={2} width={"100%"} justifyContent={"end"}>
            <Button onClick={handleClose} variant="outlined" label="Cancel" size="large" />
            {displayPeMetricsForm ? (
              <Button
                type="submit"
                form={FINLAND_METRICS_FORM_ID}
                variant="contained"
                autoFocus
                label="Validate PE metrics"
                size="large"
                startIcon={<CreateIcon />}
                disabled={isLoading}
              />
            ) : (
              <Button
                onClick={handleFinalSubmit}
                variant="contained"
                label="Confirm"
                size="large"
                startIcon={<CreateIcon />}
                disabled={isLoading}
              />
            )}
          </Stack>
        }
      >
        {displayAssociationOperationsSummaryOnly && <AssociationOperationsSummary data={associationValues} />}
        {displayAssociationAndMetricOperationsSummary && (
          <Stack direction="column" gap={2}>
            <AssociationOperationsSummary data={associationValues} />
            {metricValues && <MetricsSummary data={metricValues} />}
          </Stack>
        )}
        {displayPeMetricsForm && (
          <PeMetricsFormContainer siteServiceInfos={siteFinlandServiceInfo} handleSubmit={handleSavePeMetrics} />
        )}
      </CustomDialog>
    </>
  );
};
